/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author jayah
 * 06/11/2024
 *
 */


import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

class FederationsForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            // Main federation fields
            fedUserMappingId: null,
            fedExternalUserId: '',
            fedIesUserId: '',
            fedIdpUserId: '',
            fedSmsId: '',
            fedPartnerId: '',
            fedInstitutionId: '',
            fedToolProxyGuid: '',
            fedCreatedDate: '',
            isSMSLinked: '',
            isIESLinked: '',
            isDisplayLoader: true,
            errors: {},
            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'External User ID',
                    inputName: 'fedExternalUserId',
                    placeholder: 'Please enter External User ID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'IES User ID',
                    inputName: 'fedIesUserId',
                    placeholder: 'Please enter IES User ID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'IDP User ID',
                    inputName: 'fedIdpUserId',
                    placeholder: 'Please enter IDP User ID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'SMS ID',
                    inputName: 'fedSmsId',
                    placeholder: 'Please enter SMS ID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Partner ID',
                    inputName: 'fedPartnerId',
                    placeholder: 'Please enter Partner ID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Institution ID',
                    inputName: 'fedInstitutionId',
                    placeholder: 'Please enter Institution ID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Tool Proxy GUID',
                    inputName: 'fedToolProxyGuid',
                    placeholder: 'Please enter Tool Proxy GUID here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'Created Date',
                    inputName: 'fedCreatedDate',
                    placeholder: 'Please enter Created Date here',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'SMS Linked',
                    inputName: 'isSMSLinked',
                    required: false,
                    disabled: true
                },
                {
                    inputType: 'text',
                    labelName: 'IES Linked',
                    inputName: 'isIESLinked',
                    required: false,
                    disabled: true
                }
            ]
        };

        const fedUserMappingId = this.props.match.params.id;

        if (fedUserMappingId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'federations',
                pageName: "Federations",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/federations",
                optionalIcons: ['backIcon', 'clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'federations',
                pageName: "Federations",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/federations",
                optionalIcons: ['backIcon', 'clearFormIcon', 'deleteIcon']
            });
        }

        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {

        EventEmitter.on('unlinkFormData', this.handleUnlinkConfirmation);

        const fedUserMappingId = this.props.match.params.id;
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = { ...this.state };

        if (fedUserMappingId !== undefined) {
            const url = '/federation-record/mapping?search=%7BfedUserMappingId:' + fedUserMappingId + '%7D';

            await API.get(url, { headers: { Authorization: token } }).then(res => {
                const data = res.data[0];
                oldState.fedUserMappingId = data.fedUserMappingId || null;
                oldState.fedExternalUserId = data.fedExternalUserId || '';
                oldState.fedIesUserId = data.fedIesUserId || '';
                oldState.fedIdpUserId = data.fedIdpUserId || '';
                oldState.fedSmsId = data.fedSmsId || '';
                oldState.fedPartnerId = data.fedPartnerId || '';
                oldState.fedInstitutionId = data.fedInstitutionId || '';
                oldState.fedToolProxyGuid = data.fedToolProxyGuid || '';
                oldState.fedCreatedDate = data.fedCreatedDate || '';
                oldState.isSMSLinked = data.smslinked ? 'Available' : 'Removed';
                oldState.isIESLinked = data.ieslinked ? 'Available' : 'Removed';
                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls(er);
            });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        EventEmitter.off('unlinkFormData', this.handleUnlinkConfirmation);
    };

    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    handleSubmit = () => {
        let data = {
            fedUserMappingId: this.state.fedUserMappingId,
            fedExternalUserId: this.state.fedExternalUserId,
            fedIesUserId: this.state.fedIesUserId,
            fedIdpUserId: this.state.fedIdpUserId,
            fedSmsId: this.state.fedSmsId,
            fedPartnerId: this.state.fedPartnerId,
            fedInstitutionId: this.state.fedInstitutionId,
            fedToolProxyGuid: this.state.fedToolProxyGuid,
            fedCreatedDate: this.state.fedCreatedDate,
            isSMSLinked: this.state.isSMSLinked,
            isIESLinked: this.state.isIESLinked
        };

        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            if (this.state.fedUserMappingId !== null) {
                this.unlinkFederation(data);
            } else {
                this.unlinkFederation(data);
            }
        }
    };

    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'fedExternalUserId':
                if (value === '')
                    error[key] = "External User ID is Required!";
                else if (value === ' ')
                    error[key] = "External User ID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "External User ID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedIesUserId':
                if (value === '')
                    error[key] = "IES User ID is Required!";
                else if (value === ' ')
                    error[key] = "IES User ID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "IES User ID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedIdpUserId':
                if (value === '')
                    error[key] = "IDP User ID is Required!";
                else if (value === ' ')
                    error[key] = "IDP User ID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "IDP User ID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedSmsId':
                if (value === '')
                    error[key] = "SMS ID is Required!";
                else if (value === ' ')
                    error[key] = "SMS ID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "SMS ID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedPartnerId':
                if (value === '')
                    error[key] = "Partner ID is Required!";
                else if (value === ' ')
                    error[key] = "Partner ID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Partner ID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedInstitutionId':
                if (value === '')
                    error[key] = "Institution ID is Required!";
                else if (value === ' ')
                    error[key] = "Institution ID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Institution ID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedToolProxyGuid':
                if (value === '')
                    error[key] = "Tool Proxy GUID is Required!";
                else if (value === ' ')
                    error[key] = "Tool Proxy GUID can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Tool Proxy GUID can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'fedCreatedDate':
                if (value === '')
                    error[key] = "Created Date is Required!";
                else if (value === ' ')
                    error[key] = "Created Date can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Created Date can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = { ...this.state };
        newState.errors = { ...newState.errors, ...error };

        this.setState(newState);
    };

    unlinkFederation = async (data) => {
        await API.post('/federation-record/unlink',
            {
                unlinkUserMappingId: data.fedUserMappingId,
                unlinkIdpUserId: data.fedIdpUserId,
                unlinkIesUserId: data.fedIesUserId,
                unlinkSmsId: data.fedSmsId,
                unlinkExternalUserId: data.fedExternalUserId
            }, { headers: { Authorization: this.state.token } }
        ).then(res => {
            this.setAlert("Success", "Success", "The User has been successfully Unlinked from IES.");
            this.props.history.push('/home/federations');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };


    displayDeleteWarningMessage = () => {
        if (this.state.fedUserMappingId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'federations',
                name: this.state.fedExternalUserId
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the federation",
                alertTitle: "Error"
            });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Federation already exists!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    clearForm = () => {
        const newState = { ...this.state };

        newState.fedExternalUserId = "";
        newState.fedIesUserId = "";
        newState.fedIdpUserId = "";
        newState.fedSmsId = "";
        newState.fedPartnerId = "";
        newState.fedInstitutionId = "";
        newState.fedToolProxyGuid = "";
        newState.fedCreatedDate = "";
        newState.isSMSLinked = false;
        newState.isIESLinked = false;
        newState.errors = {};

        this.setState(newState);
    };

    navigateToPreviousPage = () => {
        this.props.history.push('/home/federations');
    };

    navigateToLoginPage = () => {
        localStorage.removeItem('tmcUser')
        this.props.history.push('/');
    };

    handleUnlinkClick = () => {
        if (this.state.fedUserMappingId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'unlink',
                name: this.state.fedExternalUserId
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to unlink before loading the federation",
                alertTitle: "Error"
            });
        }
    };

    handleUnlinkConfirmation = (confirm) => {
        if (confirm) {
            this.handleSubmit(); // Proceed with the unlink action
        }
    };


    render() {
        return (
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader />}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Unlink" onClick={this.handleUnlinkClick}>
                        <i className="fa fa-unlink fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        );
    }
}

export default FederationsForm;
